import React, { useState, useEffect, useCallback } from 'react';
import { X, MessageSquare, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Modal from '../common/Modal';
import CommentsList from './CommentsList';
import CommentInput from './CommentInput';
import { fetchComments, createComment } from '../../api/commentsService';

/**
 * CommentsModal - Modal component for displaying and managing comments
 * @param {boolean} isOpen - Whether the modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {string} module - Module name (e.g., 'session', 'candidate')
 * @param {string|number} objectId - ID of the object to attach comments to
 * @param {string} title - Optional custom title (defaults to "Comments")
 * @param {string} moduleName - Optional display name for the module (e.g., "Session", "Candidate")
 * @param {string} objectName - Optional name/details of the object (e.g., "Session #123", "John Doe")
 * @param {Object} objectDetails - Optional additional details object
 */
const CommentsModal = ({
  isOpen,
  onClose,
  module,
  objectId,
  title = null,
  moduleName = null,
  objectName = null,
  objectDetails = null,
}) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  // Fetch comments when modal opens
  const loadComments = useCallback(async () => {
    if (!module || !objectId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchComments(module, objectId);
      // Handle both array and object with results property
      const commentsData = Array.isArray(response) ? response : response.results || response.data || [];
      // Sort by added_date ascending (oldest first, newest at bottom - chat style)
      const sortedComments = [...commentsData].sort((a, b) => {
        return new Date(a.added_date || a.created_at) - new Date(b.added_date || b.created_at);
      });
      setComments(sortedComments);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || t('comments.errorLoading');
      setError(errorMessage);
      console.error('Failed to load comments:', err);
    } finally {
      setIsLoading(false);
    }
  }, [module, objectId, t]);

  useEffect(() => {
    if (isOpen) {
      loadComments();
    } else {
      // Reset state when modal closes
      setComments([]);
      setError(null);
      setSubmitError(null);
    }
  }, [isOpen, loadComments]);

  const handleSubmitComment = async (commentText, isInternal) => {
    if (!module || !objectId || !commentText.trim()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const newComment = await createComment(module, objectId, commentText, isInternal);
      
      // Add new comment to the end of the list (bottom - chat style)
      setComments((prev) => [...prev, newComment]);
      
      // Show success toast
      toast.success(t('comments.commentAdded'), {
        position: 'top-right',
        autoClose: 2000,
      });
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.comment?.[0] ||
                          err.message || 
                          t('comments.errorSubmitting');
      setSubmitError(errorMessage);
      console.error('Failed to submit comment:', err);
      
      // Show error toast
      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRefresh = () => {
    loadComments();
    toast.info(t('comments.refreshing'), {
      position: 'top-right',
      autoClose: 1500,
    });
  };

  // Format module display name
  const getModuleDisplayName = () => {
    if (moduleName) return moduleName;
    // Capitalize first letter of module
    return module ? module.charAt(0).toUpperCase() + module.slice(1) : '';
  };

  // Format object details
  const getObjectDetails = () => {
    if (objectName) return objectName;
    if (objectDetails) {
      // Try to get name from common fields
      if (objectDetails.title) return objectDetails.title;
      if (objectDetails.name) return objectDetails.name;
      if (objectDetails.id) return `${getModuleDisplayName()} #${objectDetails.id}`;
    }
    if (objectId) return `${getModuleDisplayName()} #${objectId}`;
    return '';
  };

  const modalTitle = title || t('comments.title');
  const displayModuleName = getModuleDisplayName();
  const displayObjectDetails = getObjectDetails();

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="max-w-2xl">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 truncate">{modalTitle}</h2>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              {displayModuleName && (
                <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                  {displayModuleName}
                </span>
              )}
              {displayObjectDetails && (
                <span className="text-xs text-gray-600 truncate">
                  {displayObjectDetails}
                </span>
              )}
              {comments.length > 0 && (
                <span className="text-xs text-gray-500">
                  • {t('comments.commentCount', { count: comments.length })}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={t('comments.refresh')}
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
            title={t('common.cancel')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Comments List */}
      <div className="flex flex-col" style={{ minHeight: '400px', maxHeight: '600px' }}>
        <CommentsList
          comments={comments}
          isLoading={isLoading}
          error={error}
        />
      </div>

      {/* Input Section */}
      <CommentInput
        onSubmit={handleSubmitComment}
        isSubmitting={isSubmitting}
        error={submitError}
      />
    </Modal>
  );
};

export default CommentsModal;

